import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShareFolderModalStore } from "src/store/layout/useShareFolderModalStore";
import useListAllShares from "src/hooks/useListAllShares";
import { PUBLIC_URL_DEMO } from "src/services/Endpoints";
import { usePublicShareLink } from "src/hooks/usePublicShareLink";
import { useAuthStore } from "src/store/api/useAuthStore";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  ScrollShadow,
  Avatar,
  Select as SelectNextUi,
  SelectItem,
  Switch,
  Input,
  Card,
  CardBody,
  Chip,
  Divider,
  Tooltip,
} from "@nextui-org/react";
import Select from "react-select";
import { GlobeIcon } from "../../assets/icons/GlobeIcon";
import useSaveShare from "src/hooks/useSaveShare";
import useListAllNotifications from "src/hooks/useListAllNotifications";
import { useLastThreeNotifications } from "src/hooks/useListLastThreeNotifications";
import useRefreshListAllSharedByMe from "src/hooks/useRefreshListAllSharedByMe";
import { CopyIcon } from "lucide-react";

interface ShareFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (names: string[], roles: string[]) => void;
  onCopyLink: () => void;
  resourceId: string;
  resourceName: string;
  resourceType: "folder";
}

const ShareFolderModal: React.FC<ShareFolderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  resourceId,
  resourceName,
  resourceType,
}) => {
  const { t } = useTranslation();
  const { permissions } = useAuthStore();
  const {
    listAllShares,
    nonSharedUsers,
    insideSharedUsers,
    outsideSharedUsers,
    outsideSharedLink,
    expiration_date,
  } = useListAllShares();
  const [hasFetched, setHasFetched] = useState(false);
  const { names, roles, setNames, setRoles, reset } =
    useShareFolderModalStore();
  const [isPublicAccess, setIsPublicAccess] = useState(false);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const [publicAccessFetching, setIsPublicAccessFetching] = useState(false);
  const { publicShareLink, fetchPublicShareLink } = usePublicShareLink({
    resourceType,
    resourceId,
  });

  const [combinedSharedUsers, setCombinedSharedUsers] = useState<any[]>([]); // Manage combined shared users state
  const [updatedNameOptions, setUpdatedNameOptions] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState<string>("employee_user");
  const [showPeopleSelect, setShowPeopleSelect] = useState(true);
  const [showClubSelect, setShowClubSelect] = useState(false);

  // Club-related state for dynamic data
  const [clubOptions, setClubOptions] = useState<any[]>([]);
  const [isClubsLoading] = useState(false);

  //save share
  const { saveShare } = useSaveShare();
  const { listAllNotificationsItems, currentPage, rowsPerPage } =
    useListAllNotifications();
  const { listLastThreeNotifications } = useLastThreeNotifications();
  const {
    refreshListAllShareByUser,
    shareByCurrentPage,
    rowsPerPage: sharedByRowsPerPage,
  } = useRefreshListAllSharedByMe();

  useEffect(() => {
    if (isOpen && resourceId && !hasFetched) {
      listAllShares("folder", resourceId).then(() => setHasFetched(true));

      if (outsideSharedUsers) {
        setIsPublicAccess(true);
        setExpirationDate(expiration_date);
        setIsPublicAccessFetching(false);
      } else {
        setIsPublicAccess(false);
        setExpirationDate(null);
        setIsPublicAccessFetching(false);
      }

      if (insideSharedUsers) {
        setCombinedSharedUsers(insideSharedUsers);
      }
    }
    if (!isOpen) {
      setHasFetched(false);
      setCombinedSharedUsers([]); // Clear combinedSharedUsers when modal closes
      setSelectedType("employee_user"); // Reset to default selection
      setShowPeopleSelect(true);
      setShowClubSelect(false);
    }
  }, [
    isOpen,
    resourceId,
    listAllShares,
    hasFetched,
    outsideSharedUsers,
    insideSharedUsers,
    expiration_date,
  ]);

  useEffect(() => {
    // Update nameOptions to exclude already shared users and filter by is_club = 0
    if (nonSharedUsers && combinedSharedUsers) {
      const combinedSharedUserIds = combinedSharedUsers.map(
        (user) => user.user_id
      );
      const updatedOptions = (nonSharedUsers || [])
        .filter((user) => !combinedSharedUserIds.includes(user.user_id) && (user as any).is_club === 0)
        .map((user) => {
          const name = user.full_name_en
            ? user.full_name_en
            : user.full_name_ar
              ? user.full_name_ar
              : user.username;
          const user_id_staff =
            user.user_id_saff != null ? " - " + user.user_id_saff : "";
          return {
            value: user.user_id,
            label: `${name} ${user_id_staff}`,
          };
        });
      setUpdatedNameOptions(updatedOptions);
    }
  }, [combinedSharedUsers, nonSharedUsers]);

  // Effect to update club options from nonSharedUsers when is_club = 1
  useEffect(() => {
    if (nonSharedUsers && combinedSharedUsers) {
      const combinedSharedUserIds = combinedSharedUsers.map(
        (user) => user.user_id
      );
      const clubOptions = (nonSharedUsers || [])
        .filter((user) => !combinedSharedUserIds.includes(user.user_id) && (user as any).is_club === 1)
        .map((club: any) => {
          // Use user_id (encrypted) as primary, fallback to club_id
          const clubId = club.user_id || club.club_id;
          const clubName = club.club_name_en || club.full_name_en || club.username;
          // Clean up person name by trimming extra spaces
          const personName = (club.person_name_en || club.full_name_en || club.username).trim();

          return {
            value: club.user_id,
            label: `${clubName} - ${personName}`,
            isClub: club.is_club === 1 // Convert number to boolean (1 = true, 0 = false)
          };
        });
      setClubOptions(clubOptions);
    } else {
      setClubOptions([]); // Clear options if no data
    }
  }, [combinedSharedUsers, nonSharedUsers]);

  // Default role options
  const roleOptions = [
    { value: "viewer", label: t("Viewer") },
    { value: "editor", label: t("Editor") },
  ];

  // Type selection options
  const typeOptions = [
    { value: "employee_user", label: t("SelectAnEmployeeOrUser") },
    { value: "club", label: t("SelectAClub") },
  ];
  const permissionOptions = [
    { value: "viewer", label: t("Viewer") },
    { value: "editor", label: t("Editor") },
    { value: "removeAccess", label: t("RemoveAccess") },
  ];

  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const handleCopyPublicLink = async () => {
    try {
      if (outsideSharedUsers && outsideSharedLink !== "") {
        await navigator.clipboard.writeText(
          `${PUBLIC_URL_DEMO}${outsideSharedLink}`
        );
        setCopyMessage(t("CopiedPublicLink"));
      } else if (publicAccessFetching) {
        await navigator.clipboard.writeText(
          `${PUBLIC_URL_DEMO}${publicShareLink}`
        );
        setCopyMessage(t("CopiedPublicLink"));
      } else if (!publicAccessFetching) {
        await fetchPublicShareLink(expirationDate); //expirationDate
        setIsPublicAccessFetching(true);
        if (publicShareLink) {
          await navigator.clipboard.writeText(
            `${PUBLIC_URL_DEMO}${publicShareLink}`
          );
          setCopyMessage(t("CopiedPublicLink"));
        }
      }
    } catch (error) {
      setIsPublicAccessFetching(false);
      setCopyMessage(t("FailedCopiedPublicLink"));
    } finally {
      setTimeout(() => setCopyMessage(null), 3000);
    }
  };

  const handleSwitchChange = (checked: boolean) => {
    setIsPublicAccess(checked);
  };

  const handleTypeChange = (selectedOption: any) => {
    setSelectedType(selectedOption?.value || "");
    setShowPeopleSelect(selectedOption?.value === "employee_user");
    setShowClubSelect(selectedOption?.value === "club");
    // Reset selections when type changes
    setNames([]);
    setRoles([]);
  };



  const handleSave = async () => {
    try {
      handleAdd(true); // This function is no longer used

      const sharePayload = combinedSharedUsers.map((user) => ({
        shared_with_user_id: user.user_id,
        share_type: "inside",
        permissions: user.permissions,
        expiration_date: expirationDate || null,
      }));

      // if (isPublicAccess && !publicAccessFetching && ( (!outsideSharedUsers && outsideSharedLink === "") || expirationDate !== expiration_date ) ) {

      if (
        isPublicAccess &&
        ((!outsideSharedUsers && outsideSharedLink === "") ||
          expirationDate !== expiration_date)
      ) {
        sharePayload.push({
          shared_with_user_id: null, // or some default value if required
          share_type: "outside",
          permissions: "viewer", // or any default permission you want to set
          expiration_date: expirationDate, //set the expiration_date
        });
      } else if (!isPublicAccess) {
        sharePayload.push({
          shared_with_user_id: null, // or some default value if required
          share_type: "outside",
          permissions: "removePublicAccess", // or any default permission you want to set
          expiration_date: null,
        });
      }

      await saveShare(resourceType, resourceId, sharePayload);
      await listAllNotificationsItems(currentPage, rowsPerPage); // Refresh the notifications after marking them as read
      await listLastThreeNotifications();
      await refreshListAllShareByUser(shareByCurrentPage, sharedByRowsPerPage);

      onSave(names, roles);
      reset();
      onClose();
    } catch (err) {
      console.error("Error saving shares: ", err);
    }
  };




  const getRoleColor = (role: string) => {
    switch (role) {
      case "editor":
        return "success";
      case "viewer":
        return "primary";
      case "removeAccess":
        return "danger";
      default:
        return "default";
    }
  };

  const LockIcon = (props: any) => (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      {...props}
    >
      <path d="M12 2a5 5 0 00-5 5v4H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 5a3 3 0 016 0v4H9V7zm-1 6h8v7H8v-7z" />
    </svg>
  );

  const UnlockIcon = (props: any) => (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="currentColor"
      {...props}
    >
      <path d="M18 8h-1V7a5 5 0 00-10 0h2a3 3 0 016 0v4H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2zm0 11H6v-7h12v7z" />
    </svg>
  );


  const handleAdd = (withSave: boolean) => {
    if (selectedType === "employee_user") {
      // Find users to add
      const newUsers = updatedNameOptions.filter((option) =>
        names.includes(option.value)
      );

      if (newUsers.length > 0) {
        // Check if roles are selected; if not, set default role to "Viewer"
        const newRole = roles.length > 0 ? roles[0] : "viewer"; // Default role is "viewer"

        const updatedUsers = newUsers.map((user) => ({
          user_id: user.value,
          full_name_en: user.label.split(" - ")[0],
          permissions: newRole, // Set permissions to the selected or default role
        }));

        if (withSave) combinedSharedUsers.push(...updatedUsers);
        else
          setCombinedSharedUsers((prevUsers) => [
            ...prevUsers,
            ...updatedUsers,
          ]);

        setNames([]);
        setRoles([]);
      }
    } else if (selectedType === "club") {
      // Find club to add
      const selectedClub = clubOptions.find((option) =>
        names.includes(option.value)
      );

      if (selectedClub) {
        const newRole = roles.length > 0 ? roles[0] : "viewer"; // Default role is "viewer"

        const newClub = {
          user_id: selectedClub.value,
          full_name_en: selectedClub.label,
          permissions: newRole,
          isClub: true, // Flag to identify this is a club
        };

        if (withSave) combinedSharedUsers.push(newClub);
        else setCombinedSharedUsers((prevUsers) => [...prevUsers, newClub]);

        setNames([]);
        setRoles([]);
      }
    }
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: "0.5rem",
      backgroundColor: "#F4F4F5",
      borderColor: "transparent",
      boxShadow: "none",
      marginTop: "1.25rem",
      "&:hover": {
        borderColor: "transparent",
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: "white",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "black",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#73737C",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: "#73737C",
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <Modal scrollBehavior="inside" size="5xl" isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          {t("Share")} {resourceName}:{" "}
        </ModalHeader>
        <ModalBody>
          <form className="flex flex-col gap-4 pb-10">
            {/* Type Selection - Only show if user has can_share_with_club permission */}
            {permissions?.can_share_with_club && (
              <div className="flex gap-3 max-sm:flex-col">
                <Select
                  options={typeOptions}
                  value={typeOptions.find(
                    (option) => option.value === selectedType
                  )}
                  onChange={handleTypeChange}
                  placeholder={t("ShareWith")}
                  styles={customStyles}
                  className="w-full"
                  isClearable
                />
              </div>
            )}

            {/* Conditional Selection based on Type */}
            {showPeopleSelect && (
              <div className="flex gap-3 max-sm:flex-col">
                {/* People to Share With section */}
                <Select
                  isMulti
                  options={updatedNameOptions}
                  value={updatedNameOptions.filter((option) =>
                    names.includes(option.value)
                  )}
                  onChange={(selectedOptions) =>
                    setNames(
                      selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : []
                    )
                  }
                  placeholder={t("AddPeople")}
                  styles={customStyles}
                  className="w-1/3 max-sm:w-full"
                />
                {/* Role section */}
                <Select
                  options={roleOptions}
                  value={roleOptions.find(
                    (option) => option.value === roles[0]
                  )} // Only one value is allowed
                  onChange={(selectedOption) =>
                    setRoles(
                      selectedOption ? [selectedOption.value] : [] // Set single value or empty array
                    )
                  }
                  placeholder={t("SelectRoles")}
                  styles={customStyles}
                  className="w-1/2 max-sm:w-full"
                  isClearable // Allows the user to clear the selection
                />
                <Button
                  className="mt-5"
                  color="primary"
                  onPress={() => handleAdd(false)}
                >
                  {t("Add")}
                </Button>
              </div>
            )}

            {showClubSelect && (
              <div className="flex gap-3 max-sm:flex-col">
                {/* Club Selection section */}
                <Select
                  options={clubOptions}
                  value={clubOptions.find(
                    (option) => option.value === names[0]
                  )}
                  onChange={(selectedOption) =>
                    setNames(selectedOption ? [selectedOption.value] : [])
                  }
                  placeholder={t("SelectClub")}
                  styles={customStyles}
                  className="w-1/3 max-sm:w-full"
                  isLoading={isClubsLoading}
                />
                {/* Role section for clubs */}
                <Select
                  options={roleOptions}
                  value={roleOptions.find(
                    (option) => option.value === roles[0]
                  )}
                  onChange={(selectedOption) =>
                    setRoles(selectedOption ? [selectedOption.value] : [])
                  }
                  placeholder={t("SelectRoles")}
                  styles={customStyles}
                  className="w-1/2 max-sm:w-full"
                  isClearable
                />
                <Button
                  className="mt-5"
                  color="primary"
                  onPress={() => handleAdd(false)}
                >
                  {t("Add")}
                </Button>
              </div>
            )}

            {/* People With Access section */}
            <div>
              <div className="flex items-center justify-start gap-3 mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  {t("PeopleWithAccess")}
                </h4>
                <Chip size="sm" variant="flat" color="default">
                  {combinedSharedUsers?.length || 0}
                </Chip>
              </div>

              <Card className="border-0 shadow-sm">
                <CardBody className="p-2">
                  <ScrollShadow hideScrollBar className="w-full max-h-[350px]">
                    <div className="space-y-3 p-4">
                      {combinedSharedUsers?.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg
                              className="w-8 h-8 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                              />
                            </svg>
                          </div>
                          <p className="text-sm">{t("NoUsersHaveAccessYet")}</p>
                        </div>
                      ) : (
                        combinedSharedUsers.map((user) => (
                          <Card
                            key={user.user_id}
                            className="border border-gray-200 hover: border-secondary/20 hover:shadow-md transition-all duration-200 bg-white"
                          >
                            <CardBody className="p-4">
                              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                                {/* User info */}
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                  <Avatar
                                    src=""
                                    size="md"
                                    className="bg-gradient-to-br from-success/10 to-success/30 text-success"
                                    name={
                                      user.full_name_en?.[0] ||
                                      user.full_name_ar?.[0] ||
                                      user.username?.[0]
                                    }
                                  />
                                  <div className="flex flex-col min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-gray-900 truncate">
                                        {user.full_name_en || user.full_name_ar || user.username}
                                      </span>
                                      {user.isClub && (
                                        <Chip
                                          size="sm"
                                          color="secondary"
                                          variant="flat"
                                        >
                                          {t("Club")}
                                        </Chip>
                                      )}
                                    </div>
                                    {!user.isClub && user.user_id_saff && (
                                      <span className="text-gray-500 text-sm">
                                        {user.user_id_saff}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center w-full lg:w-auto">
                                  {/* Role select */}
                                  <div className="w-full sm:w-40">
                                    <SelectNextUi
                                      label={t("Role")}
                                      size="sm"
                                      value={user.permissions}
                                      key={user.permissions} // Ensure this matches one of the SelectItem values
                                      placeholder={t(user.permissions.charAt(0).toUpperCase() + user.permissions.slice(1))} // Ensure this matches one of the SelectItem values
                                      color={getRoleColor(user.permissions)}
                                      variant="bordered"
                                      className="w-full"
                                      onChange={(e) => {
                                        const newRole = e.target.value;
                                        setCombinedSharedUsers((prev) =>
                                          prev.map((u) =>
                                            u.user_id === user.user_id
                                              ? {
                                                ...u,
                                                permissions: newRole,
                                                isLocked:
                                                  newRole === "editor"
                                                    ? u.isLocked ?? false
                                                    : undefined,
                                                startDate:
                                                  newRole === "editor"
                                                    ? u.startDate ?? null
                                                    : null,
                                                endDate:
                                                  newRole === "editor"
                                                    ? u.endDate ?? null
                                                    : null,
                                              }
                                              : u
                                          )
                                        );
                                      }}
                                    >
                                      {permissionOptions.map((role) => (
                                        <SelectItem
                                          key={role.value}
                                          value={role.value}
                                        >
                                          {role.label}
                                        </SelectItem>
                                      ))}
                                    </SelectNextUi>
                                  </div>

                                  {/* Editor-specific controls */}
                                  {/* {user.permissions === "editor" && (
                                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center w-full lg:w-auto">
                                      <div className="flex items-center gap-2">
                                        <Tooltip
                                          content={
                                            <div className="px-1 py-1">
                                              <div className="font-medium mb-1">
                                                {user.isLocked ? t("AccessLocked") : t("AccessUnlocked")}
                                              </div>
                                              <div className="text-xs text-gray-900">
                                                {user.isLocked
                                                  ? t("LockAccessDescription")
                                                  : t("UnlockAccessDescription")
                                                }
                                              </div>
                                            </div>
                                          }
                                          placement="top-start"
                                          color="default"
                                          closeDelay={0}
                                        >
                                          <Switch
                                            size="md"
                                            color="primary"
                                            isSelected={user.isLocked || false}
                                            onChange={(e) => {
                                              const checked = e.target.checked;
                                              setCombinedSharedUsers((prev) =>
                                                prev.map((u) =>
                                                  u.user_id === user.user_id
                                                    ? { ...u, isLocked: checked }
                                                    : u
                                                )
                                              );
                                            }}
                                            thumbIcon={({
                                              isSelected,
                                              className,
                                            }) =>
                                              isSelected ? (
                                                <LockIcon className={className} />
                                              ) : (
                                                <UnlockIcon
                                                  className={className}
                                                />
                                              )
                                            }
                                          />
                                        </Tooltip>
                                      </div>

                                      {!user.isLocked && (
                                        <div className="flex flex-col sm:flex-row gap-2">
                                          <Tooltip
                                            content={
                                              <div className="px-1 py-2">
                                                <div className="font-medium mb-1">{t("LockStartDate")}</div>
                                                <div className="text-xs text-gray-300">
                                                  {t("LockStartDateDescription")}
                                                </div>
                                              </div>
                                            }
                                            placement="top-start"
                                            color="primary"
                                            closeDelay={0}
                                          >
                                            <Input
                                              type="date"
                                              size="sm"
                                              label={t("Start")}
                                              variant="bordered"
                                              value={user.startDate || ""}
                                              className="w-full sm:w-36"
                                              onChange={(e) => {
                                                const date = e.target.value;
                                                setCombinedSharedUsers((prev) =>
                                                  prev.map((u) =>
                                                    u.user_id === user.user_id
                                                      ? { ...u, startDate: date }
                                                      : u
                                                  )
                                                );
                                              }}
                                            />
                                          </Tooltip>
                                          <Tooltip
                                            content={
                                              <div className="px-1 py-2">
                                                <div className="font-medium mb-1">{t("LockEndDate")}</div>
                                                <div className="text-xs text-gray-300">
                                                  {t("LockEndDateDescription")}
                                                </div>
                                              </div>
                                            }
                                            placement="top-start"
                                            color="danger"
                                            closeDelay={0}
                                            showArrow={true}
                                          >
                                            <Input
                                              type="date"
                                              size="sm"
                                              label={t("End")}
                                              variant="bordered"
                                              isRequired
                                              value={user.endDate || ""}
                                              className="w-full sm:w-36"
                                              onChange={(e) => {
                                                const date = e.target.value;
                                                setCombinedSharedUsers((prev) =>
                                                  prev.map((u) =>
                                                    u.user_id === user.user_id
                                                      ? { ...u, endDate: date }
                                                      : u
                                                  )
                                                );
                                              }}
                                            />
                                          </Tooltip>
                                        </div>
                                      )}
                                    </div>
                                  )} */}
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))
                      )}
                    </div>
                  </ScrollShadow>
                </CardBody>
              </Card>
            </div>

            <Divider />

            {/* Public Access section */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50">
              <CardBody className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <GlobeIcon />
                    </div>
                    <div className="text-start">
                      <h4 className="text-lg font-semibold text-gray-800">
                        {t("PublicAccess")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {t("ShareViaPublicLink")}
                      </p>
                    </div>
                  </div>
                  <Switch
                    color="warning"
                    size="lg"
                    isSelected={isPublicAccess}
                    onChange={(e) => handleSwitchChange(e.target.checked)}
                  />
                </div>

                {copyMessage && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">{copyMessage}</p>
                  </div>
                )}

                {isPublicAccess && (
                  <div className="space-y-4 pt-4 border-t border-amber-200">
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-amber-200 text-start">
                          <Avatar
                            icon={<CopyIcon className="w-4 h-4" />}
                            className="bg-amber-100 text-black flex-shrink-0"
                            size="sm"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-700 mb-2">
                              {t("ByCopyingTheLinkYouAgreeToShareItOutsideSaf")}
                            </p>
                            <p className="text-xs text-red-500">
                              {t("ByCopyingTheLinkYouAreResponsible")}
                            </p>
                          </div>
                        </div>
                      </div>
                      <Button
                        color="warning"
                        variant="solid"
                        onPress={handleCopyPublicLink}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg sm:flex-shrink-0"
                        size="lg"
                      >
                        {t("CopyLink")}
                      </Button>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 items-end">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                          {t("ExpirationDate")}
                        </label>
                        <Input
                          type="date"
                          variant="bordered"
                          value={expirationDate || ""}
                          onChange={(e) => setExpirationDate(e.target.value)}
                          className="w-full"
                          placeholder="Select expiration date"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onClose}>
            {t("Close")}
          </Button>
          <Button color="primary" onPress={handleSave}>
            {t("Save")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareFolderModal;
